import { BellIcon, CheckCheckIcon, CircleAlertIcon, SettingsIcon } from 'lucide-react';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { EmptyState } from '@/shared/components/empty-state';
import { ListSkeleton } from '@/shared/components/skeletons';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import { useMarkAllRead, useNotifications, useUnreadCount, type NotificationFilter } from '../api';
import { setNotificationNavigator } from '../prefs';

import { NotificationItem } from './NotificationItem';

/** Shown in the dropdown; the page lists everything. */
const DROPDOWN_LIMIT = 8;

function BellPanel({
  onClose,
  panelRef,
}: {
  onClose: () => void;
  panelRef: RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation('notifications');
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const unread = useUnreadCount().data ?? 0;
  const list = useNotifications({ filter, module: null });
  const markAll = useMarkAllRead();
  const items = (list.data?.pages.flatMap((p) => p.items) ?? []).slice(0, DROPDOWN_LIMIT);

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      className="flex max-h-[min(36rem,calc(100dvh-5rem))] flex-col outline-none"
      data-testid="notifications-panel"
    >
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">{t('panel.title')}</h2>
        <div className="ml-auto flex items-center gap-0.5">
          <SimpleTooltip content={t('panel.markAllRead')}>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('panel.markAllRead')}
              disabled={unread === 0 || markAll.isPending}
              onClick={() => markAll.mutate(null)}
              data-testid="notifications-mark-all"
            >
              <CheckCheckIcon />
            </Button>
          </SimpleTooltip>
          <SimpleTooltip content={t('panel.settings')}>
            <Button variant="ghost" size="icon-sm" asChild>
              <Link to="/notifications/settings" aria-label={t('panel.settings')} onClick={onClose}>
                <SettingsIcon />
              </Link>
            </Button>
          </SimpleTooltip>
        </div>
      </div>
      <div className="px-4 pt-3">
        <Tabs value={filter} onValueChange={(value) => setFilter(value as NotificationFilter)}>
          <TabsList className="w-full">
            <TabsTrigger value="all">{t('filters.all')}</TabsTrigger>
            <TabsTrigger value="unread">
              {t('filters.unread')}
              {unread > 0 && (
                <span className="rounded-full bg-brand-soft px-1.5 text-[0.6875rem] font-semibold text-brand-text tabular-nums">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2">
        {list.isPending ? (
          <ListSkeleton rows={4} className="px-2" />
        ) : list.isError ? (
          <div className="flex flex-col items-center gap-3 px-4 py-8 text-center text-sm text-muted-foreground">
            <CircleAlertIcon className="size-5 text-destructive-text" />
            {t('errors.loadTitle')}
            <Button variant="outline" size="sm" onClick={() => void list.refetch()}>
              {t('actions.retry')}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            size="sm"
            icon={filter === 'unread' ? CheckCheckIcon : BellIcon}
            title={filter === 'unread' ? t('empty.unreadTitle') : t('empty.allTitle')}
            description={filter === 'unread' ? t('empty.unreadDescription') : t('empty.allDescription')}
          />
        ) : (
          <div className="flex flex-col gap-0.5">
            {items.map((notification) => (
              <NotificationItem key={notification.id} notification={notification} onOpened={onClose} />
            ))}
          </div>
        )}
      </div>
      <div className="border-t p-2">
        <Button variant="ghost" className="w-full" asChild>
          <Link to="/notifications" onClick={onClose} data-testid="notifications-show-all">
            {t('panel.showAll')}
          </Link>
        </Button>
      </div>
    </div>
  );
}

/** Top-bar bell: unread counter (live) and a dropdown with the latest notifications. */
export function NotificationsBell() {
  const { t } = useTranslation('notifications');
  const [open, setOpen] = useState(false);
  const unread = useUnreadCount().data ?? 0;
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  // Pop-ups and desktop notifications open links through the router.
  useEffect(() => setNotificationNavigator((path) => void navigate(path)), [navigate]);

  const label = unread > 0 ? t('widget.labelUnread', { count: unread }) : t('widget.label');
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <SimpleTooltip content={t('widget.label')}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={label}
            data-testid="notifications-bell"
            data-unread={unread}
            className={cn('relative text-muted-foreground', open && 'bg-accent text-foreground')}
          >
            <BellIcon className="size-[1.125rem]" />
            {unread > 0 && (
              <span
                data-testid="notifications-badge"
                className="absolute top-1 right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[0.625rem] leading-none font-semibold text-destructive-foreground tabular-nums ring-2 ring-surface"
              >
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </Button>
        </PopoverTrigger>
      </SimpleTooltip>
      <PopoverContent
        align="end"
        className="w-[min(26rem,calc(100vw-1rem))] p-0"
        onOpenAutoFocus={(event) => {
          // Focus the panel itself: the first button's tooltip would pop up on open.
          event.preventDefault();
          panelRef.current?.focus();
        }}
      >
        <BellPanel onClose={() => setOpen(false)} panelRef={panelRef} />
      </PopoverContent>
    </Popover>
  );
}
